/**
 * <pg-product-card> behaviour (markup: snippets/pg-product-card.liquid).
 *
 * - Quick add: single-variant products go straight to the cart (button spins, then shows a
 *   check). Products with options open the quick-add dialog, rendered server-side by
 *   sections/pg-quick-add.liquid through the Section Rendering API; if that section isn't
 *   available the shopper simply goes to the product page.
 * - Shared-element page transition: the clicked card's image is named `pg-product-media`
 *   so the product page gallery morphs out of it (View Transitions, where supported).
 */
import { PgElement, define, reducedMotion } from '@pg/core';
import { addItems } from '@pg/cart';
import { fetchSection } from '@pg/section-renderer';
import { openDialog } from '@pg/ui';

class PgProductCard extends PgElement {
  mount() {
    this.querySelector('[data-pg-quick-add]')?.addEventListener('click', (event) => this.#quickAdd(event), {
      signal: this.signal,
    });
    this.addEventListener('click', (event) => this.#nameTransition(event), { signal: this.signal });
  }

  /** @param {MouseEvent} event */
  async #quickAdd(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (button.getAttribute('aria-busy') === 'true') return;

    if (button.hasAttribute('data-single')) {
      button.setAttribute('aria-busy', 'true');
      try {
        await addItems([{ id: Number(button.dataset.variantId), quantity: 1 }], { context: 'product', source: this });
        button.classList.add('is-done');
        setTimeout(() => button.classList.remove('is-done'), 1600);
      } catch {
        /* @pg/cart already showed the error */
      } finally {
        button.removeAttribute('aria-busy');
      }
      return;
    }

    button.setAttribute('aria-busy', 'true');
    try {
      const html = await fetchSection('pg-quick-add', this.dataset.url, { key: 'quick-add' });
      const dialog = document.getElementById('pg-quick-add');
      const body = dialog?.querySelector('[data-pg-quick-add-body]');
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pg-quick-add-content]');
      if (!dialog || !body || !next) throw new Error('quick add unavailable');
      body.replaceChildren(next);
      // Scripts inside inserted HTML don't run: import the modules the content declares
      // (same URLs, so already-loaded modules are reused).
      await Promise.all(
        [...next.querySelectorAll('script[type="module"][src]')].map((script) => import(script.getAttribute('src') || '')),
      );
      body.dispatchEvent(new CustomEvent('pg:content:rendered', { bubbles: true }));
      openDialog('pg-quick-add', button);
    } catch {
      location.href = this.dataset.url || '/';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {MouseEvent} event */
  #nameTransition(event) {
    if (reducedMotion() || !('startViewTransition' in document)) return;
    const link = /** @type {Element} */ (event.target).closest('a[href]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    // Only one element per page may carry the name.
    document.querySelectorAll('.pg-card__image').forEach((img) => (/** @type {HTMLElement} */ (img).style.viewTransitionName = ''));
    const image = /** @type {HTMLElement|null} */ (this.querySelector('.pg-card__image'));
    if (image) image.style.viewTransitionName = 'pg-product-media';
  }
}

define('pg-product-card', PgProductCard);
